// Mobile Blog — 2026-09-14 v1.4 修正:
//   Mobile Blog = Blog Viewer(編集 / 削除 / 複製 / 公開切替 / AI生成 全不做)
//   - 数据源:复用 PC 版 blogApi.listPosts(同 PC Blog 管理页,GET /api/blog/posts)
//   - 搜索:title 部分一致(API 无 search 参数 → 前端本地过滤)
//   - 状态筛选:すべて / 公開 / 下書き(API status filter 可选)
//   - PC 限定提示:記事の作成・編集・公開設定はPC版でご利用ください。
//   - 无限滚动:IntersectionObserver + page += 1,hasMore = items.length >= PAGE_SIZE
//   - 每条卡片只允许一个操作:Webで開く(公開 + link あり)/ disabled 文案
//   - 不显示假 author / 不显示 PV(后端无此字段,不做假)
// 不调新增 API / 不动 PC / 不动 Router / 不动 MobileBlogEditPage(仍可 URL 直打ち,但无入口)。

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, RefreshCw, Search } from "lucide-react";
import { blogApi, type BlogPost } from "../../api/blog";

type StatusFilter = "all" | "publish" | "draft";

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: "すべて",
  publish: "公開",
  draft: "下書き",
};

// WP REST API status (publish / draft / future / private / pending) → Mobile 表示
const STATUS_BADGE: Record<
  string,
  { label: string; className: "published" | "draft" }
> = {
  publish: { label: "公開", className: "published" },
  draft: { label: "下書き", className: "draft" },
  future: { label: "予約公開", className: "draft" },
  private: { label: "非公開", className: "draft" },
  pending: { label: "保留", className: "draft" },
};

const PAGE_SIZE = 20;

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const MobileBlogPage = () => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState<BlogPost[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);

  // 初回 load(page 1)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    blogApi
      .listPosts({
        page: 1,
        per_page: PAGE_SIZE,
        status: "any",
        exclude: ["trash"],
      })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setHasMore(data.items.length >= PAGE_SIZE);
        setPage(1);
      })
      .catch(() => {
        if (!cancelled) setLoadError("記事の取得に失敗しました");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // toast 自動消失
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  // 追加 load(page N)
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || refreshing || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const data = await blogApi.listPosts({
        page: nextPage,
        per_page: PAGE_SIZE,
        status: "any",
        exclude: ["trash"],
      });
      setItems((prev) => [...prev, ...data.items]);
      setHasMore(data.items.length >= PAGE_SIZE);
      setPage(nextPage);
    } catch {
      // 静默
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, refreshing, hasMore, page]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    if (!hasMore || loadingMore || refreshing) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void handleLoadMore();
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loadingMore, refreshing, handleLoadMore]);

  // 刷新(refreshPosts + 重 fetch page 1)
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await blogApi.refreshPosts(true);
      const data = await blogApi.listPosts({
        page: 1,
        per_page: PAGE_SIZE,
        status: "any",
        exclude: ["trash"],
      });
      setItems(data.items);
      setHasMore(data.items.length >= PAGE_SIZE);
      setPage(1);
      setToast("更新しました");
    } catch {
      setToast("更新に失敗しました");
    } finally {
      setRefreshing(false);
    }
  };

  // 前端本地过滤(title 部分一致 + status)
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return items.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (kw && !p.title.toLowerCase().includes(kw)) return false;
      return true;
    });
  }, [items, keyword, statusFilter]);

  return (
    <>
      {/* AppBar:左空 / 中 ブログ管理 / 右刷新 */}
      <header className="mobile-appbar">
        <div className="mobile-appbar-left" />
        <h1 className="mobile-appbar-title">ブログ管理</h1>
        <div className="mobile-appbar-actions">
          <button
            type="button"
            className="mobile-appbar-icon-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="更新"
            title={refreshing ? "更新中..." : "更新"}
          >
            <RefreshCw
              size={20}
              strokeWidth={1.8}
              className={refreshing ? "spin" : ""}
            />
          </button>
        </div>
      </header>

      <main className="mobile-content">
        {/* 検索 */}
        <div className="mobile-search">
          <Search size={16} color="#94a3b8" strokeWidth={2} />
          <input
            type="text"
            className="mobile-search-input"
            placeholder="記事を検索..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>

        {/* 状态 filter chips */}
        <div className="mobile-filter-chips">
          {(["all", "publish", "draft"] as const).map((s) => (
            <button
              key={s}
              type="button"
              className={`mobile-filter-chip ${statusFilter === s ? "active" : ""}`}
              onClick={() => setStatusFilter(s)}
            >
              {STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {/* PC 限定提示 */}
        <div className="mobile-blog-notice">
          <span>記事の作成・編集・公開設定はPC版でご利用ください。</span>
        </div>

        {/* List */}
        {loading && items.length === 0 ? (
          <div className="mobile-loading">
            <span className="mobile-spinner" />
            読み込み中...
          </div>
        ) : loadError ? (
          <div className="mobile-loading" style={{ color: "var(--mobile-danger)" }}>
            {loadError}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mobile-loading">該当する記事がありません</div>
        ) : (
          <div className="mobile-blog-list">
            {filtered.map((post) => {
              const badge = STATUS_BADGE[post.status] || {
                label: post.status,
                className: "draft" as const,
              };
              const canOpen = post.status === "publish" && !!post.link;
              return (
                <article key={post.id} className="mobile-blog-card">
                  <div className="mobile-blog-card-header">
                    <span
                      className={`mobile-blog-status ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  </div>
                  <h3 className="mobile-blog-title">{post.title}</h3>
                  <div className="mobile-blog-meta">
                    <span>作成：{formatDateTime(post.date_gmt)}</span>
                    <span>更新：{formatDateTime(post.modified_gmt)}</span>
                  </div>
                  <div className="mobile-blog-actions">
                    {canOpen ? (
                      <a
                        href={post.link!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mobile-blog-open-btn"
                      >
                        <ExternalLink size={14} strokeWidth={2} />
                        Webで開く
                      </a>
                    ) : (
                      <span className="mobile-blog-open-btn mobile-blog-open-btn--disabled">
                        <ExternalLink size={14} strokeWidth={2} />
                        {post.status === "draft"
                          ? "下書きはPC版で確認してください"
                          : "公開URLがありません"}
                      </span>
                    )}
                  </div>
                </article>
              );
            })}

            <div ref={loadMoreRef} className="mobile-blog-sentinel" />
            {loadingMore && (
              <div className="mobile-blog-loadmore">さらに読み込み中...</div>
            )}
            {!hasMore && items.length > 0 && (
              <div className="mobile-blog-loadmore-end">
                これ以上記事はありません
              </div>
            )}
          </div>
        )}
      </main>

      {toast && <div className="mobile-toast">{toast}</div>}
    </>
  );
};